import './ChatBot.scss';

import {
  Avatar,
  Box,
  Button,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowRight,
  ArrowUp,
  CaretDown,
  Chats,
  Check,
  CheckCircle,
  Copy,
  Gear,
  Globe,
  Link,
  Microphone,
  Notebook,
  NotePencil,
  Pause,
  Play,
  Plus,
  Sidebar,
  SpeakerHigh,
  SpeakerSlash,
  Stop,
  TextAlignJustify,
  Trash,
  XCircle,
} from '@phosphor-icons/react';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactMarkdown from 'react-markdown';
import { useLocation, useParams } from 'react-router-dom';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { io, Socket } from 'socket.io-client';
import { IChatbotFlowModel } from '../../data/models/chatbot';
import {
  IFlowChatActions,
  IFlowModel,
  MessageDictionary,
} from '../../data/models/flow';
import { IFluxBotModel } from '../../data/models/flux-bot';
import { ChatStreamEvent } from '../../data/responses/chatbot';
import { ChatbotService } from '../../service/ChatbotService';
import { FlowService } from '../../service/FlowService';
import { FluxService } from '../../service/FluxService';
import { GroqService } from '../../service/GroqService';
import { UserService } from '../../service/UserService';
import { useAuthStore } from '../../store/storeAuth';
import { useNotificationStore } from '../../store/storeNotifications';
import { useStoreTabs } from '../../store/storeTabs';
import { User } from '../../util/Types/User';
import { flowToChatbotMapper } from '../../utils/flow-to-chatbot-mapper';
import { generateRandomId } from '../../utils/generateId';
import LoadingSpinner from '../UI/LoadingSpinner';
import { CodeFlyout } from './_components/CodeFlyout';
import { FluxbotDropdown } from './_components/Fluxbot-dropdown';
import { History } from './_components/History';
import {
  ChatContainer,
  ChatContent,
  ChatHeader,
  FlyoutArea,
  MainChatArea,
  MessageBubble,
  MessageContainer,
  MessagesContainer,
  SplitScreenContainer,
  TypingIndicator,
} from './_components/Styled-components';
import { WaveformVisualizer } from './_components/WaveformVisualizer';
import { ChatbotCodeComponent } from './CodeComponent';
import { LinkComponent } from './LinkComponent';
import PublishChatModal from './PublishChatModal/PublishChatModal';

type ExpansionLevel = 'middle' | 'full';

export type ActiveTab = {
  id: string;
  name?: string;
  space?: string;
};

type Props = {
  onCreateChat: () => void;
  authUser: User;
  sideBarOpenWidth: number;
};

interface IChat {
  id: string;
  name: string;
  entries: IEntry[];
}

interface IEntry {
  role: string;
  content: string;
  timestamp: string;
  actions?: IFlowChatActions[];
  outputId?: string;
}

export interface IFlyout {
  version: number;
  messageTimestamp: string;
  contents: {
    content: string;
    type: 'code' | 'audio' | 'image' | 'link' | 'flyout-output';
  }[];
}

const MemoizedLinkComponent = memo(LinkComponent);

const MessageContent = memo(
  ({
    content,
    messageTimestamp,
    isNewMessage,
    isChatRespondingWithVoice,
    onOpenFlyout,
    chatbot,
    disableFlyoutButtons = false,
  }: {
    content: string;
    messageTimestamp: string;
    isNewMessage: boolean;
    isChatRespondingWithVoice: boolean;
    onOpenFlyout?: (
      code: string,
      language: string,
      content?: string,
      timestamp?: string,
      href?: string,
    ) => void;
    chatbot: any;
    disableFlyoutButtons?: boolean;
  }) => {
    return (
      <ReactMarkdown
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
        remarkPlugins={[remarkGfm]}
        components={{
          img: ({ node, ...props }) => (
            <img
              {...props}
              alt={props.alt ?? 'Image'}
              style={{ width: '100%', borderRadius: '8px', marginTop: '8px' }}
            />
          ),
          audio: ({ node, ...props }) => (
            <>
              <audio controls style={{ width: '100%', marginTop: '8px' }}>
                <track kind="captions" />
                <source src={props.src} />
                Your browser does not support the audio element.
              </audio>
              <br />
            </>
          ),
          a: props => (
            <MemoizedLinkComponent
              {...props}
              id={messageTimestamp}
              autoPlay={isChatRespondingWithVoice}
              isNewMessage={isNewMessage}
              chatbot={chatbot}
              messageContent={content}
              onOpenLinksFlyout={
                disableFlyoutButtons
                  ? undefined
                  : (content: string, type: 'image' | 'audio' | 'link', href?: string) => {
                      if (onOpenFlyout) {
                        onOpenFlyout('', type, content, messageTimestamp, href);
                      }
                    }
              }
            />
          ),
          code: props => (
            <ChatbotCodeComponent
              {...props}
              onOpenFlyout={
                disableFlyoutButtons
                  ? undefined
                  : (code, language, timestamp) => {
                      if (onOpenFlyout) {
                        onOpenFlyout(
                          code,
                          language,
                          undefined,
                          timestamp || messageTimestamp,
                          undefined,
                        );
                      }
                    }
              }
              messageTimestamp={messageTimestamp}
              chatbot={chatbot}
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    );
  },
);

const ChatBot = ({ onCreateChat, authUser, sideBarOpenWidth }: Props) => {
  const { setNotification } = useNotificationStore((state: any) => state);
  const { setAuthUser } = useAuthStore((state: any) => state);
  const location = useLocation();
  const params = useParams
  ();
  const tabs = useStoreTabs((state: any) => state.tabs);

  const chatAudioSpeakerRef = useRef<HTMLAudioElement>(null);

  const isAbleToShowTabs = location.pathname.toLowerCase().includes('agent');

  const audioRecognitionRef = useRef<SpeechRecognition>();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const tabDropdownRef = useRef<HTMLDivElement>(null);
  const settingsPopoverAnchorEl = useRef<HTMLButtonElement | null>(null);
  const historyPopoverAnchorEl = useRef<HTMLButtonElement | null>(null);
  const hasProcessedDrawerOpen = useRef(false);

  const [socketClient, setSocketClient] = useState<Socket | null>(null);

  const [inputValue, setInputValue] = useState('');
  const [isLoadingFluxBots, setIsLoadingFluxBots] = useState(false);
  const [isLoadingChatbot, setIsLoadingChatbot] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isHistoryPopoverOpen, setIsHistoryPopoverOpen] = useState(false);
  const [isSettingsPopoverOpen, setIsSettingsPopoverOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isFluxbotDropdownPopoverOpen, setIsFluxbotDropdownPopoverOpen] =
    useState(false);
  const [expasionMode, setExpasionMode] = useState<ExpansionLevel>('middle');
  const [activeTab, setActiveTab] = useState<ActiveTab>({
    id: '',
    name: '',
    space: '',
  });

  const [isTyping, setIsTyping] = useState(false);
  const [isReceivingStreamingContent, setIsReceivingStreamingContent] =
    useState(false);

  const [fluxBots, setFluxBots] = useState<IFluxBotModel[]>([]);

  const [chatbot, setChatbot] = useState<IChatbotFlowModel>(
    {} as IChatbotFlowModel,
  );

  const chatbotRef = useRef<IChatbotFlowModel>(chatbot);
  const sessionRef = useRef<string>('default');

  const [dropdownValues, setDropdownValues] = useState<string[]>([]);

  const [session, setSession] = useState<string>('default');
  const [receivedStreamContentStorage, setReceivedStreamContentStorage] =
    useState<string>('');

  const [isRecording, setIsRecording] = useState(false);
  const [isChatRespondingWithVoice, setIsChatRespondingWithVoice] =
    useState(false);

  const [isGeneratingTTS, setIsGeneratingTTS] = useState(false);
  const [isLastChatResponseTTSGenerated, setIsLastChatResponseTTSGenerated] =
    useState(false);
  const [isChatSpeaking, setIsChatSpeaking] = useState(false);

  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const [currentFlyoutVersion, setCurrentFlyoutVersion] = useState<
    number | null
  >(null);
  const previousSessionRef = useRef<string>(session);

  const [contents, setContents] = useState<MessageDictionary>(
    {} as MessageDictionary,
  );

  const toggleDrawer = () => {
    setIsOpen(!isOpen);
  };

  const toggleExpansionMode = () => {
    setExpasionMode(prev => (prev === 'middle' ? 'full' : 'middle'));
  };

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }, 300);
  }, []);

  const saveChatFlowId = useCallback(async (flowId: string) => {
    if (!authUser || !flowId) return;

    try {
      await UserService.updateUser({
        chatFlowId: flowId,
      });

      setAuthUser({
        ...authUser,
        chatFlowId: flowId,
      });
    } catch (error) {
      console.error('Error saving chatFlowId:', error);
    }
  }, [authUser, setAuthUser]);

  const getFluxBots = async () => {
    setIsLoadingFluxBots(true);
    try {
      const res = await FlowService.getFluxBots();

      setActiveTab({
        id: res.data[0].id ?? '',
        name: res.data[0].name ?? '',
        space: res.data[0].space ?? '',
      });

      setFluxBots(res.data);
    } catch (error: any) {
      console.error(error);
      setNotification({
        type: 'error',
        message: error?.message ?? 'Error while fetching flux bots',
      });
    } finally {
      setIsLoadingFluxBots(false);
    }
  };

  const getChatbot = async () => {
    setIsLoadingChatbot(true);
    try {
      const chatbotResponse = await ChatbotService.getChatbot(activeTab.id);
      setDropdownValues([]);

      if (
        !chatbotResponse.data.sessions.some(
          session => session.id.toLowerCase() === 'default',
        )
      ) {
        chatbotResponse.data.sessions.push({
          id: 'default',
          name: 'Primary',
          entries: [],
        });
      }

      if (
        chatbotResponse.data.chatbotDropdowns &&
        chatbotResponse.data.chatbotDropdowns.length > 0
      ) {
        setDropdownValues(
          chatbotResponse.data.chatbotDropdowns.map(
            item => item.options[0].value,
          ),
        );
      }

      setChatbot(chatbotResponse.data);
      setContents(
        chatbotResponse.data.flyOutContents ?? ({} as MessageDictionary),
      );
      const firstSession =
        chatbotResponse.data.sessions.find(
          s => s.id.toLowerCase() === 'default',
        ) ?? chatbotResponse.data.sessions[0];
      setSession(firstSession?.id ?? 'default');
    } catch (error: any) {
      console.error(error);
      setNotification({
        type: 'error',
        message: error?.message ?? 'Something went wrong',
      });
    } finally {
      setIsLoadingChatbot(false);
    }
  };

  const getChatSession = (sessionId: string) => {
    try {
      const chatSession = chatbot.sessions.find(
        session => session.id === sessionId,
      );

      if (chatSession) {
        setSession(chatSession.id);
      } else {
        const newSessionId =
          sessionId === 'default' ? 'default' : generateRandomId();

        const newChat: IChat = {
          id: newSessionId,
          name: newSessionId === 'default' ? 'Primary' : newSessionId,
          entries:
            chatbot.firstMessage && chatbot.storeOpeningMessage !== 'as-title'
              ? [
                  {
                    role: 'assistant',
                    content: chatbot.firstMessage,
                    timestamp: new Date().toISOString(),
                    actions: chatbot.chatActions ?? [],
                  },
                ]
              : [],
        };

        setChatbot(prev => ({
          ...prev,
          sessions: [...prev.sessions, newChat],
        }));
        setSession(newSessionId);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handlePublish = async () => {
    try {
      const response = await FlowService.updateFlow(chatbot.id, {
        public: !chatbot.public,
      });

      const newChatbot = flowToChatbotMapper(response.data);

      setChatbot(newChatbot);

      setNotification({
        type: 'success',
        message: 'Published successfully',
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Error publishing',
      });
      console.error(error);
    }
  };

  const handleClearConversation = async () => {
    if (!chatbot) {
      setNotification({
        type: 'error',
        message: 'Chatbot not set',
      });
      return;
    }

    try {
      await FlowService.clearChatFlow(chatbot.id, session);

      setChatbot(prev => {
        const updatedSessions = prev.sessions.map(chatSession => {
          if (chatSession.id === session) {
            return {
              ...chatSession,
              entries: [],
            };
          }

          return chatSession;
        });

        return { ...prev, sessions: updatedSessions };
      });

      setContents(prev => ({
        ...prev,
        [session]: [],
      }));
      setCurrentFlyoutVersion(null);
      setIsFlyoutOpen(false);

      setNotification({
        type: 'success',
        message: 'Chat cleared successfully',
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Error clearing chat',
      });
      console.error(error);
    }
  };

  const handleCreateNewChat = () => {
    const sessionId = generateRandomId();

    const newChat: IChat = {
      id: sessionId,
      name: sessionId,
      entries:
        chatbot.firstMessage && chatbot.storeOpeningMessage !== 'as-title'
          ? [
              {
                role: 'assistant',
                content: chatbot.firstMessage,
                timestamp: new Date().toISOString(),
                actions: chatbot.chatActions ?? [],
              },
            ]
          : [],
    };

    setSession(sessionId);
    setChatbot(prev => ({
      ...prev,
      sessions: [...prev.sessions, newChat],
    }));
  };

  const handleCopyMessage = (message: string, messageId: string) => {
    navigator.clipboard.writeText(message);
    setCopiedMessageId(messageId);

    setTimeout(() => {
      setCopiedMessageId(null);
    }, 2000);
  };

  const handleRecord = () => {
    try {
      const SpeechRecognition =
        window.SpeechRecognition ?? window.webkitSpeechRecognition;

      if (!SpeechRecognition) {
        console.error('Speech recognition not supported in this browser');
        return;
      }

      audioRecognitionRef.current = new SpeechRecognition();
      audioRecognitionRef.current.continuous = true;
      audioRecognitionRef.current.lang = 'en-US';

      audioRecognitionRef.current.onstart = () => {
        setInputValue('');
        setIsRecording(true);
      };

      audioRecognitionRef.current.onend = () => {
        setIsRecording(false);
      };

      audioRecognitionRef.current.onerror = event => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
      };

      audioRecognitionRef.current.onresult = event => {
        const resultsLength = event.results.length;
        let transcript = '';
        setInputValue('');

        for (let i = 0; i < resultsLength; i++) {
          const result = event.results[i];
          transcript += result[0].transcript;
        }

        setInputValue(transcript);
      };

      audioRecognitionRef.current.start();
    } catch (error) {
      console.error('Error initializing speech recognition:', error);
      setIsRecording(false);
    }
  };

  const handleStopRecording = () => {
    if (!audioRecognitionRef.current) return;

    audioRecognitionRef.current.stop();
    setIsRecording(false);
  };

  const handleVoiceToggle = () => {
    setIsChatRespondingWithVoice(prev => {
      return !prev;
    });
  };

  const hasAudioLink = (text: string): boolean => {
    if (text.match(/\.(mp3|wav|ogg|m4a|aac|flac|webm)/i)) {
      return true;
    }

    return false;
  };

  const handleSpeak = useCallback(
    (fileUrl: any | null) => {
      try {
        if (!fileUrl) return;

        if (chatAudioSpeakerRef.current && !isChatSpeaking) {
          const blob = new Blob([fileUrl.data], { type: 'audio/wav' });
          const audioUrl = URL.createObjectURL(blob);

          chatAudioSpeakerRef.current.src = audioUrl;
          chatAudioSpeakerRef.current.currentTime = 0;
          chatAudioSpeakerRef.current.play();
        }
      } catch (err) {
        console.error('BLOB ERROR:', err);
      }
    },
    [isChatSpeaking],
  );

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendMessage = async (message?: string) => {
    if (!chatbot) {
      setNotification({
        type: 'error',
        message: 'Chatbot not set',
      });
      return;
    }

    if (!inputValue.trim() && !message) return;

    const newMessage = {
      role: 'user',
      content: message ?? inputValue.trim(),
      timestamp: new Date().toISOString(),
    };

    setIsTyping(true);
    setIsLastChatResponseTTSGenerated(false);
    try {
      await FluxService.chatApi(chatbot.id, socketClient?.id!, {
        sessionId: session,
        variableInputs: [
          {
            inputText: newMessage.content,
          },
          ...dropdownValues.map((value, index) => ({
            inputId: chatbot.chatbotDropdowns?.[index]?.varInputNodeId,
            inputText: value,
          })),
        ],
      });

      setChatbot(prev => ({
        ...prev,
        sessions: prev.sessions.map(chatSession => {
          if (chatSession.id === session) {
            return {
              ...chatSession,
              entries: [...chatSession.entries, newMessage],
            };
          }

          return chatSession;
        }),
      }));

      setInputValue('');
    } catch (err) {
      console.log(err);
    }
  };

  const handleSetChatbot = (flow: IFlowModel) => {
    const chatbot = flowToChatbotMapper(flow);

    const sessionExists = chatbot.sessions.find(ses => ses.id === session);

    if (sessionExists) {
      setSession(sessionExists.id);
    } else {
      setSession(chatbot.sessions[0]?.id ?? 'default');
    }

    setChatbot(chatbot);
  };

  const handleGenerateTTS = useCallback(async () => {
    try {
      setIsGeneratingTTS(true);
      const chatSession = chatbot.sessions.find(ses => ses.id === session);

      if (!chatSession || chatSession.entries.length === 0) return null;

      const lastChatResponse =
        chatSession.entries[chatSession.entries.length - 1].content;

      const voiceResponse = await GroqService.voiceGenerateBuffer(
        chatbot.chatbotVoice!,
        {
          text: lastChatResponse,
          modelId: 'canopylabs/orpheus-v1-english',
        },
      );

      return voiceResponse;
    } catch (err) {
      return null;
    } finally {
      setIsGeneratingTTS(false);
    }
  }, [chatbot, session]);

  const handlePlayChatResponse = useCallback(async () => {
    if (isChatSpeaking) {
      chatAudioSpeakerRef.current?.pause();
      return;
    }

    if (!isLastChatResponseTTSGenerated) {
      const tts = await handleGenerateTTS();

      handleSpeak(tts);
      setIsLastChatResponseTTSGenerated(true);
      return;
    }

    chatAudioSpeakerRef.current!.currentTime = 0;
    chatAudioSpeakerRef.current?.play();
  }, [
    isChatSpeaking,
    isLastChatResponseTTSGenerated,
    handleGenerateTTS,
    handleSpeak,
  ]);

  const generateVoiceBufferForStream = async () => {
    const voiceResponse = await GroqService.voiceGenerateBuffer(
      chatbot.chatbotVoice!,
      {
        text: receivedStreamContentStorage,
        modelId: 'canopylabs/orpheus-v1-english',
      },
    );

    return voiceResponse;
  };

  useEffect(() => {
    if (!isOpen) {
      hasProcessedDrawerOpen.current = false;
      return;
    }

    if (isOpen && fluxBots.length > 0 && !hasProcessedDrawerOpen.current) {
      hasProcessedDrawerOpen.current = true;
      
      const flowId = params.flowId;
      const searchParams = new URLSearchParams(location.search);
      const typeParam = searchParams.get('type');

      let selectedFluxBot: IFluxBotModel | null = null;

      if (flowId && typeParam === 'chat' && location.pathname.includes('/flow/')) {
        selectedFluxBot = fluxBots.find(bot => bot.id === flowId) || null;
        
        if (selectedFluxBot) {
          setActiveTab({
            id: selectedFluxBot.id,
            name: selectedFluxBot.name ?? '',
            space: selectedFluxBot.space ?? '',
          });
          saveChatFlowId(selectedFluxBot.id);
          return;
        }
      }

      if (!selectedFluxBot && authUser?.chatFlowId) {
        selectedFluxBot = fluxBots.find(bot => bot.id === authUser.chatFlowId) || null;
        
        if (selectedFluxBot) {
          setActiveTab({
            id: selectedFluxBot.id,
            name: selectedFluxBot.name ?? '',
            space: selectedFluxBot.space ?? '',
          });
          return;
        }
      }

      if (!selectedFluxBot && fluxBots.length > 0) {
        const defaultFluxBot = fluxBots[0];
        setActiveTab({
          id: defaultFluxBot.id ?? '',
          name: defaultFluxBot.name ?? '',
          space: defaultFluxBot.space ?? '',
        });
      }
    }
  }, [isOpen, location.pathname, location.search, params.flowId, fluxBots.length, authUser?.chatFlowId]);

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [isOpen, scrollToBottom, isTyping, chatbot, session]);

  useEffect(() => {
    chatbotRef.current = chatbot;
  }, [chatbot]);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    if (activeTab.id) {
      getChatbot();
    }
  }, [activeTab]);

  useEffect(() => {
    getFluxBots();
  }, []);

  useEffect(() => {
    const handleChatbotCreated = () => {
      getFluxBots();
    };

    window.addEventListener('chatbotCreated', handleChatbotCreated);

    return () => {
      window.removeEventListener('chatbotCreated', handleChatbotCreated);
    };
  }, []);

  useEffect(() => {
    const socket = io(import.meta.env.VITE_API_URL as string);

    socket.on('connect', () => {
      setSocketClient(socket);
    });

    socket.on('chatbot-req', async (socketResponse: ChatStreamEvent) => {
      const currentChatbot = chatbotRef.current;
      const currentSession = sessionRef.current;

      if (!currentChatbot?.id) {
        console.warn(
          'Chatbot não está inicializado ainda. Ignorando evento do socket.',
        );
        return;
      }

      let generatedVoice: any = null;
      const chatbotOutputNodeId = currentChatbot.chatbotDataOutputNodeId;

      if (socketResponse.data.type === 'stream') {
        const socketData = socketResponse.data;

        switch (socketData.event) {
          case 'stream_start':
            setIsReceivingStreamingContent(true);
            setChatbot(prev => {
              const updatedSessions = prev.sessions.map(chatSession => {
                if (chatSession.id === currentSession) {
                  return {
                    ...chatSession,
                    entries: [
                      ...chatSession.entries,
                      {
                        role: 'assistant',
                        content: '',
                        timestamp: socketData.data.timestamp,
                      },
                    ],
                  };
                }

                return chatSession;
              });

              return { ...prev, sessions: updatedSessions };
            });

            setReceivedStreamContentStorage('');
            break;
          case 'stream_error':
            break;
          case 'stream_thinking':
            break;
          case 'stream_message':
            setChatbot(prev => {
              const updatedSessions = prev.sessions.map(chatSession => {
                if (chatSession.id === currentSession) {
                  const entryIndex = chatSession.entries.findIndex(
                    entry => entry.timestamp === socketData.data.timestamp,
                  );

                  const newEntries = [...chatSession.entries];

                  newEntries[entryIndex] = {
                    ...newEntries[entryIndex],
                    content:
                      newEntries[entryIndex].content + socketData.data.text,
                  };

                  return {
                    ...chatSession,
                    entries: newEntries,
                  };
                }

                return chatSession;
              });

              return { ...prev, sessions: updatedSessions };
            });

            setReceivedStreamContentStorage(old => old + socketData.data.text);

            break;
          case 'stream_end':
            if (
              isChatRespondingWithVoice &&
              !hasAudioLink(receivedStreamContentStorage)
            ) {
              const voiceResponse = await generateVoiceBufferForStream();

              generatedVoice = voiceResponse;
              setIsLastChatResponseTTSGenerated(true);
            }

            setIsReceivingStreamingContent(false);
            break;
          default:
            break;
        }
      } else {
        const socketData = socketResponse.data;

        switch (socketData.event) {
          case 'message':
            if (!chatbotOutputNodeId) {
              console.warn(
                'Chatbot sem output node configurado. Ignorando evento message.',
              );
              setIsTyping(false);
              return;
            }
            const chatbotOutputMessage = socketData.data?.filter(
              (data: { outputId: string }) =>
                data.outputId === chatbotOutputNodeId,
            );

            if (
              isChatRespondingWithVoice &&
              !hasAudioLink(chatbotOutputMessage[0].text)
            ) {
              const voiceResponse = await GroqService.voiceGenerateBuffer(
                currentChatbot.chatbotVoice!,
                {
                  text: chatbotOutputMessage[0].text,
                  modelId: 'canopylabs/orpheus-v1-english',
                },
              );

              generatedVoice = voiceResponse;
              setIsLastChatResponseTTSGenerated(true);
            }

            const messageTimestamp = socketData.data[0].timestamp;

            setChatbot(prev => {
              const updatedSessions = prev.sessions.map(chatSession => {
                if (chatSession.id === currentSession) {
                  const newEntries = [...chatSession.entries];

                  const newMessage = {
                    role: 'assistant',
                    content: socketData.data[0].text,
                    timestamp: messageTimestamp,
                    outputId: currentChatbot.chatbotDataOutputNodeId,
                  };
                  newEntries.push(newMessage);

                  const contentSource = currentChatbot.contentSource;
                  if (
                    contentSource === 'output-node' ||
                    contentSource === 'both'
                  ) {
                    const flyOutMessage = socketData.data?.find(
                      (data: { outputId: string }) =>
                        data.outputId === currentChatbot.flyOutDataOutputNodeId,
                    );

                    if (flyOutMessage) {
                      const flyOutEntry = {
                        role: 'assistant',
                        content: flyOutMessage.text,
                        timestamp: messageTimestamp,
                        outputId: currentChatbot.flyOutDataOutputNodeId,
                      };
                      newEntries.push(flyOutEntry);
                    }
                  }

                  return {
                    ...chatSession,
                    entries: newEntries,
                  };
                }

                return chatSession;
              });

              return { ...prev, sessions: updatedSessions };
            });

            break;
          case 'error':
            setNotification({
              type: 'error',
              message: socketData.data.error,
            });
            break;
          default:
            break;
        }
      }

      handleSpeak(generatedVoice);
      setIsTyping(false);
    });

    socket.on('chatbot-flyout', async (socketResponse: any) => {
      const currentChatbot = chatbotRef.current;
      const currentSession = sessionRef.current;

      if (currentChatbot?.enableChatFlyout) {
        setContents(prev => {
          if (prev[currentSession]) {
            return {
              ...prev,
              [currentSession]: [...prev[currentSession], socketResponse.data.message],
            };
          }
          return {
            ...prev,
            [currentSession]: [socketResponse.data.message],
          };
        });

        setIsFlyoutOpen(!isFlyoutOpen);
      }
    });

    return () => {
      socket?.off('connect');
      socket?.off('chatbot-req');
      socket?.off('chatbot-flyout');
    };
  }, [isChatRespondingWithVoice]);

  useEffect(() => {
    if (!isChatRespondingWithVoice) {
      chatAudioSpeakerRef.current?.pause();
    }
  }, [isChatRespondingWithVoice]);

  useEffect(() => {
    const audio = chatAudioSpeakerRef.current;

    if (!audio) return;

    audio.addEventListener('ended', () => {
      setIsChatSpeaking(false);
    });

    audio.addEventListener('pause', () => {
      setIsChatSpeaking(false);
    });

    audio.addEventListener('play', () => {
      setIsChatSpeaking(true);
    });

    return () => {
      audio?.removeEventListener('play', () => {});

      audio?.removeEventListener('ended', () => {});

      audio?.removeEventListener('pause', () => {});
    };
  }, [chatAudioSpeakerRef.current]);

  useEffect(() => {
    setIsLastChatResponseTTSGenerated(false);
  }, [session]);

  useEffect(() => {
    if (
      !chatbot.sessions ||
      chatbot.sessions.length === 0 ||
      !chatbot.flyOutContents
    )
      return;

    const currentSessionData = chatbot.sessions.find(ses => ses.id === session);
    if (!currentSessionData) return;

    const isFirstLoad =
      previousSessionRef.current === 'default' && session === 'default';
    if (!isFirstLoad && previousSessionRef.current !== session) {
      setIsFlyoutOpen(false);
    }
    previousSessionRef.current = session;
  }, [session, chatbot.sessions]);

  const currentSession = useMemo(() => {
    if (!chatbot.sessions || chatbot.sessions.length === 0) return null;

    return chatbot.sessions.find(chat => chat.id === session) ?? null;
  }, [chatbot, session]);

  const filteredEntries = useMemo(() => {
    if (!currentSession?.entries) return [];

    const contentSource = chatbot.contentSource;

    return (currentSession.entries as IEntry[]).filter(entry => {
      if (entry.role === 'user') return true;

      if (entry.role === 'assistant') {
        const entryWithOutputId = entry as IEntry;
        const outputId = entryWithOutputId.outputId;

        if (contentSource === 'main-chat') {
          return !outputId || outputId === chatbot.chatbotDataOutputNodeId;
        }

        if (contentSource === 'output-node') {
          return !outputId || outputId === chatbot.chatbotDataOutputNodeId;
        }

        if (contentSource === 'both') {
          return !outputId || outputId === chatbot.chatbotDataOutputNodeId;
        }
      }

      return true;
    });
  }, [
    currentSession,
    chatbot.contentSource,
    chatbot.chatbotDataOutputNodeId,
    chatbot.flyOutContents,
  ]);

  const settingsOptions = [
    {
      key: 'clear-chat',
      label: 'Clear chat',
      icon: <Trash size={24} weight="regular" />,
      action: handleClearConversation,
      disabled: false,
    },
    {
      key: 'edit-bot',
      label: 'Edit FluxBot',
      icon: <NotePencil size={24} weight="regular" />,
      action: () => {
        setIsSettingsOpen(true);
      },
      disabled: false,
    },
    {
      key: 'auto-play',
      label: `Autoplay Audio ${
        isChatRespondingWithVoice ? 'Enabled' : 'Disabled'
      }`,
      icon: isChatRespondingWithVoice ? (
        <SpeakerHigh size={24} weight="regular" />
      ) : (
        <SpeakerSlash size={24} weight="regular" />
      ),
      action: handleVoiceToggle,
      disabled: false,
    },
    {
      key: 'publish-external',
      label: 'Publish externally',
      icon: <Play size={24} weight="regular" />,
      action: handlePublish,
      disabled: !!chatbot.public,
    },
    {
      key: 'copy-link',
      label: 'Copy link',
      icon: <Link size={24} weight="regular" />,
      action: () => {
        const linkChat = `${window.location.origin}/chatbot/${chatbot.id}`;
        navigator.clipboard.writeText(linkChat);
      },
      disabled: !chatbot.public,
    },
    {
      key: 'go-to-chat',
      label: 'Go to FluxBot',
      icon: <Globe size={24} weight="regular" />,
      action: () => {
        window.open(
          `${window.location.origin}/chatbot/${chatbot.id}`,
          '_blank',
        );
      },
      disabled: !chatbot.public,
    },
    {
      key: 'create-chat',
      label: 'Create new Chat',
      icon: <Plus size={24} weight="regular" />,
      action: handleCreateNewChat,
      disabled: false,
    },
  ];

  if (!isAbleToShowTabs) return null;

  return (
    <Box>
      <audio
        key="chatbot-audio-speaker"
        ref={chatAudioSpeakerRef}
        style={{ display: 'hidden', width: 0, height: 0 }}
      />

      <Box
        sx={{
          position: 'absolute',
          top: tabs.length <= 1 ? '8px' : '45px',
          right: '20px',
        }}
      >
        <IconButton onClick={toggleDrawer}>
          <Chats size={20} color="#3650DE" />
        </IconButton>
      </Box>

      <Drawer
        variant="persistent"
        anchor="right"
        open={isOpen}
        onClose={toggleDrawer}
        hideBackdrop
        PaperProps={{
          sx: {
            width:
              expasionMode === 'middle'
                ? isFlyoutOpen
                  ? '1050px'
                  : '600px'
                : window.innerWidth - sideBarOpenWidth,
            padding: 0,
            maxWidth: '100%',
            borderTop: `6px solid ${chatbot.brandColor}`,
            marginTop: tabs.length <= 1 ? '40px' : '80px',
            height:
              tabs.length <= 1 ? 'calc(100vh - 40px)' : 'calc(100vh - 80px)',
            maxHeight:
              tabs.length <= 1 ? 'calc(100vh - 40px)' : 'calc(100vh - 80px)',
            borderRadius: '8px 0 0 0',
            transition: 'all 0.3s ease-in-out',
            overflow: 'hidden',
          },
        }}
      >
        {isLoadingFluxBots && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <LoadingSpinner />
          </Box>
        )}

        {!isLoadingFluxBots && (
          <ChatContainer
            sx={{
              background: chatbot.pageBackground,
              color: chatbot.pageText,
              backgroundImage: `url(${chatbot.backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              fontFamily: chatbot.font,
              fontWeight: chatbot.fontWeight,
              paddingBottom: 0,
              padding: 0,
            }}
          >
            <SplitScreenContainer>
              {isFlyoutOpen && (
                <FlyoutArea
                  sx={{
                    width: expasionMode === 'full' ? '50%' : '450px',
                    minWidth: expasionMode === 'full' ? 'auto' : '450px',
                    flexShrink: 0,
                  }}
                >
                  <CodeFlyout
                    onClose={() => setIsFlyoutOpen(false)}
                    chatbot={chatbot}
                    currentVersion={currentFlyoutVersion}
                    onVersionChange={setCurrentFlyoutVersion}
                    contents={contents[session]}
                  />
                </FlyoutArea>
              )}

              <MainChatArea
                sx={{
                  width: isFlyoutOpen
                    ? expasionMode === 'full'
                      ? '50%'
                      : '600px'
                    : '100%',
                  minWidth:
                    isFlyoutOpen && expasionMode === 'middle'
                      ? '600px'
                      : 'auto',
                  flexShrink: 0,
                }}
              >
                <ChatHeader
                  sx={{
                    padding: '16px',
                    paddingBottom: '8px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '8px',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <IconButton
                      ref={historyPopoverAnchorEl}
                      onClick={() => setIsHistoryPopoverOpen(true)}
                      sx={{
                        color: chatbot.pageText,
                        '&:hover': {
                          color: chatbot.brandColor,
                        },
                      }}
                    >
                      <TextAlignJustify
                        size={20}
                        style={{
                          cursor: 'pointer',
                        }}
                      />
                    </IconButton>

                    <Box
                      ref={tabDropdownRef}
                      onClick={() => setIsFluxbotDropdownPopoverOpen(true)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        position: 'relative',
                        cursor: 'pointer',
                        backgroundColor: '#F2F4F7',
                        borderRadius: '8px',
                        padding: '8px',
                        maxWidth: '200px',
                        minWidth: '100px',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '16px',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          color: '#475467',
                          width: '100%',
                        }}
                      >
                        {`${activeTab?.name ?? ''} (${activeTab?.space ?? ''})`}
                      </Typography>

                      <CaretDown
                        cursor="pointer"
                        color="#101828"
                        size={16}
                        weight="regular"
                      />
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px',
                        padding: '8px',
                      }}
                    >
                      <Typography
                        sx={{
                          color: chatbot.public ? '#4CAF50' : '#B42318',
                        }}
                      >
                        {chatbot.public ? 'Published' : 'Unpublished'}
                      </Typography>

                      {chatbot.public ? (
                        <CheckCircle color="#4CAF50" size={16} />
                      ) : (
                        <XCircle color="#B42318" size={16} />
                      )}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <IconButton
                      onClick={toggleExpansionMode}
                      sx={{
                        color: chatbot.pageText,
                        '&:hover': {
                          color: chatbot.brandColor,
                        },
                      }}
                    >
                      <Notebook
                        size={24}
                        style={{
                          cursor: 'pointer',
                        }}
                      />
                    </IconButton>

                    <IconButton
                      disabled={
                        isTyping ||
                        isGeneratingTTS ||
                        isReceivingStreamingContent ||
                        currentSession?.entries.length === 0
                      }
                      onClick={handlePlayChatResponse}
                      sx={{
                        color: chatbot.pageText,
                        '&:hover': {
                          color: chatbot.brandColor,
                        },
                      }}
                    >
                      {isChatSpeaking ? (
                        <Pause size={24} weight="regular" />
                      ) : (
                        <Play size={24} weight="regular" />
                      )}
                    </IconButton>

                    <IconButton
                      ref={settingsPopoverAnchorEl}
                      onClick={() => setIsSettingsPopoverOpen(true)}
                      sx={{
                        color: chatbot.pageText,
                        '&:hover': {
                          color: chatbot.brandColor,
                        },
                      }}
                    >
                      <Gear cursor={'pointer'} size={24} />
                    </IconButton>

                    <IconButton
                      onClick={toggleDrawer}
                      sx={{
                        color: chatbot.pageText,
                        '&:hover': {
                          color: chatbot.brandColor,
                        },
                      }}
                    >
                      <XCircle
                        size={24}
                        style={{
                          cursor: 'pointer',
                        }}
                      />
                    </IconButton>
                  </Box>
                </ChatHeader>

                <ChatContent
                  sx={{
                    padding: '0 16px',
                    paddingBottom: '8px',
                  }}
                >
                  {isLoadingChatbot ? (
                    <LoadingSpinner />
                  ) : (
                    <>
                      {currentSession?.entries.length === 0 && (
                        <Box
                          sx={{
                            height: '80%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: '50px',
                              height: '50px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '16px',
                              padding: '5px',
                            }}
                          >
                            {chatbot.logo ? (
                              <Avatar
                                src={chatbot.logo}
                                alt={chatbot.name}
                                sx={{
                                  width: '25px',
                                  height: '25px',

                                  '&.MuiAvatar-root': {
                                    borderRadius: 0,
                                  },
                                }}
                              />
                            ) : (
                              <Chats
                                size={50}
                                style={{
                                  color: chatbot.pageText ?? '#fff',
                                }}
                              />
                            )}
                          </Box>

                          <Typography
                            variant="body1"
                            sx={{
                              maxWidth: '70%',
                              textAlign: 'center',
                              fontSize: '1.5rem',
                              lineHeight: '133%',
                              color: chatbot.pageText,
                              fontWeight: 'bold',
                            }}
                          >
                            {chatbot.storeOpeningMessage === 'as-title' &&
                            chatbot.firstMessage
                              ? chatbot.firstMessage
                              : chatbot.name}
                          </Typography>

                          {chatbot.storeOpeningMessage === 'as-title' &&
                            chatbot.chatActions &&
                            chatbot.chatActions.length > 0 && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                  mt: 2,
                                  width: '100%',
                                  maxWidth: '400px',
                                }}
                              >
                                {chatbot.chatActions.map(action => (
                                  <Button
                                    sx={{
                                      background: chatbot.brandColor,
                                      width: '100%',
                                      borderRadius: '8px',
                                      padding: '8px 24px',
                                      border: `1px solid ${chatbot.brandColor}`,
                                      color: '#fff',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      '&:hover': {
                                        background: '#fff',
                                        color: chatbot.brandColor,
                                      },
                                    }}
                                    key={action.name}
                                    onClick={() =>
                                      handleSendMessage(action.description)
                                    }
                                    disabled={isTyping}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: '0.875rem',
                                      }}
                                    >
                                      {action.name}
                                    </Typography>
                                    <ArrowRight size={16} />
                                  </Button>
                                ))}
                              </Box>
                            )}
                        </Box>
                      )}

                      <MessagesContainer>
                        {filteredEntries.map((message, index) => {
                          const isNewMessage =
                            index === filteredEntries.length - 1 &&
                            message.role === 'assistant';

                          return (
                            <MessageContainer
                              key={`${message.timestamp}-${index}`}
                              isUser={message.role === 'user'}
                            >
                              {message.role === 'assistant' && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius:
                                      chatbot.iconShape === 'circle'
                                        ? '50%'
                                        : '8px',
                                    padding: '4px',
                                  }}
                                >
                                  {chatbot.chatImage ? (
                                    <Avatar
                                      src={chatbot.chatImage}
                                      alt={chatbot.name}
                                      sx={{
                                        width: `${chatbot.iconSize}px`,
                                        height: `${chatbot.iconSize}px`,
                                        '&.MuiAvatar-root': {
                                          borderRadius:
                                            chatbot.iconShape === 'circle'
                                              ? '50%'
                                              : 0,
                                        },
                                      }}
                                    />
                                  ) : (
                                    <Chats
                                      color={chatbot.pageText ?? '#fff'}
                                      style={{
                                        width: `${chatbot.iconSize}px`,
                                        height: `${chatbot.iconSize}px`,
                                      }}
                                    />
                                  )}
                                </Box>
                              )}

                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                }}
                              >
                                <MessageBubble
                                  isUser={message.role === 'user'}
                                  sx={{
                                    backgroundColor:
                                      message.role === 'user'
                                        ? chatbot.userContainer
                                        : chatbot.responseContainer,
                                    color:
                                      message.role === 'user'
                                        ? chatbot.userText
                                        : chatbot.responseText,
                                    fontSize: '1rem',
                                  }}
                                >
                                  <div className={'chatbot-message-content'}>
                                    <MessageContent
                                      content={message.content}
                                      messageTimestamp={message.timestamp}
                                      isNewMessage={isNewMessage}
                                      isChatRespondingWithVoice={
                                        isChatRespondingWithVoice
                                      }
                                      onOpenFlyout={(
                                        code,
                                        language,
                                        content,
                                        timestamp,
                                        href,
                                      ) => {
                                        let itemType: 'code' | 'audio' | 'image' | 'link' | 'flyout-output' = 'link';
                                        
                                        if (language === 'image' || language === 'audio' || language === 'link') {
                                          itemType = language;
                                        } else if (code && language !== 'text') {
                                          itemType = 'code';
                                        } else if (href) {
                                          if (href.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) {
                                            itemType = 'image';
                                          } else if (href.match(/\.(mp3|wav|ogg|m4a)$/i)) {
                                            itemType = 'audio';
                                          } else {
                                            itemType = 'link';
                                          }
                                        }

                                        const sessionContents = contents[session] || [];
                                        let targetVersion: number | null = null;

                                        if (timestamp) {
                                          for (const version of sessionContents) {
                                            if (version.messageTimestamp === timestamp) {
                                              const hasMatchingItem = version.contents.some(item => {
                                                if (item.type !== itemType) return false;
                                                
                                                if (href && itemType !== 'code') {
                                                  return item.content.includes(href) || item.content === href;
                                                }
                                                
                                                if (itemType === 'code' && code) {
                                                  return item.content.includes(code) || item.content === code;
                                                }
                                                
                                                return true;
                                              });
                                              
                                              if (hasMatchingItem) {
                                                targetVersion = version.version;
                                                break;
                                              }
                                            }
                                          }
                                        }

                                        if (targetVersion === null && sessionContents.length > 0) {
                                          targetVersion = sessionContents[sessionContents.length - 1].version;
                                        }

                                        setCurrentFlyoutVersion(targetVersion);
                                        setIsFlyoutOpen(true);
                                      }}
                                      chatbot={chatbot}
                                      disableFlyoutButtons={
                                        chatbot.contentSource ===
                                          'output-node' &&
                                        (!('outputId' in message) ||
                                          (message as IEntry).outputId ===
                                            chatbot.chatbotDataOutputNodeId)
                                      }
                                    />
                                  </div>
                                </MessageBubble>

                                {message.role !== 'user' &&
                                  !message.actions && (
                                    <IconButton
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'transparent',
                                        width: 'fit-content',
                                        minWidth: '32px',
                                        height: '32px',
                                        borderRadius: '8px',
                                        padding: '8px',
                                        color: chatbot.responseText,
                                        '&:hover': {
                                          backgroundColor: chatbot.brandColor,
                                          color: '#fff',
                                        },
                                        ...(copiedMessageId ===
                                          message.timestamp && {
                                          backgroundColor: chatbot.brandColor,
                                          color: '#fff',
                                        }),
                                      }}
                                      onClick={() =>
                                        handleCopyMessage(
                                          message.content,
                                          message.timestamp,
                                        )
                                      }
                                    >
                                      {copiedMessageId === message.timestamp ? (
                                        <Check size={16} weight="regular" />
                                      ) : (
                                        <Copy size={16} weight="regular" />
                                      )}
                                    </IconButton>
                                  )}

                                {message.actions && (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 1,
                                    }}
                                  >
                                    {message.actions.map(action => (
                                      <Button
                                        sx={{
                                          background: chatbot.brandColor,
                                          width: 'fit-content',
                                          borderRadius: '8px',
                                          padding: '8px 24px',
                                          border: `1px solid ${chatbot.brandColor}`,
                                          color: '#fff',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'start',
                                          gap: 4,
                                          '&:hover': {
                                            background: '#fff',
                                            color: chatbot.brandColor,
                                          },
                                        }}
                                        key={action.name}
                                        disabled={isTyping}
                                        onClick={() => {
                                          handleSendMessage(action.description);
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: '0.875rem',
                                          }}
                                        >
                                          {action.name}
                                        </Typography>

                                        <ArrowRight size={16} />
                                      </Button>
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            </MessageContainer>
                          );
                        })}

                        {isTyping && (
                          <MessageBubble
                            isUser={false}
                            sx={{
                              width: 'fit-content',
                              backgroundColor: chatbot.responseContainer,
                            }}
                          >
                            <TypingIndicator>
                              <span
                                className="dot"
                                style={{
                                  backgroundColor:
                                    chatbot.responseText ?? undefined,
                                }}
                              />
                              <span
                                className="dot"
                                style={{
                                  backgroundColor:
                                    chatbot.responseText ?? undefined,
                                }}
                              />
                              <span
                                className="dot"
                                style={{
                                  backgroundColor:
                                    chatbot.responseText ?? undefined,
                                }}
                              />
                            </TypingIndicator>
                          </MessageBubble>
                        )}
                      </MessagesContainer>

                      <div style={{ height: '5px' }} ref={messagesEndRef} />
                    </>
                  )}
                </ChatContent>

                <Box
                  sx={{
                    padding: '16px',
                    background: chatbot.backgroundImage
                      ? 'transparent'
                      : 'inherit',
                    flexShrink: 0,
                  }}
                >
                  {isRecording ? (
                    <WaveformVisualizer
                      handleStopRecording={handleStopRecording}
                      isRecording={isRecording}
                      backgroundColor={chatbot.responseContainer ?? '#fff'}
                      waveColor={chatbot.brandColor ?? '#000'}
                    />
                  ) : (
                    <>
                      <TextField
                        fullWidth
                        variant="outlined"
                        multiline
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                        placeholder={
                          chatbot.placeholder ?? 'Type your message...'
                        }
                        maxRows={4}
                        onKeyDown={handleKeyPress}
                        disabled={isLoadingChatbot || isTyping}
                        InputProps={{
                          sx: {
                            color: chatbot.responseText,
                            fontFamily: chatbot.font,
                            fontWeight: chatbot.fontWeight,
                            '-webkit-text-fill-color': chatbot.responseText,
                          },
                          startAdornment: (
                            <InputAdornment position="start">
                              {chatbot.enableChatFlyout && (
                                <IconButton
                                  onClick={() => setIsFlyoutOpen(!isFlyoutOpen)}
                                  disabled={
                                    !contents[session] ||
                                    contents[session].length === 0
                                  }
                                >
                                  <Sidebar
                                    size={20}
                                    weight="regular"
                                    style={{
                                      color: chatbot.brandColor ?? 'primary',
                                    }}
                                  />
                                </IconButton>
                              )}
                              <IconButton
                                onClick={handleRecord}
                                disabled={isLoadingChatbot || isTyping}
                              >
                                {isRecording ? (
                                  <Stop
                                    color="red"
                                    size={20}
                                    weight="regular"
                                  />
                                ) : (
                                  <Microphone
                                    style={{
                                      color: chatbot.brandColor ?? 'primary',
                                    }}
                                    size={20}
                                    weight="regular"
                                  />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button
                                variant="contained"
                                onClick={() => handleSendMessage()}
                                disabled={
                                  isLoadingChatbot ||
                                  isTyping ||
                                  !inputValue.trim()
                                }
                                sx={{
                                  margin: 0,
                                  padding: 0,
                                  minWidth: 0,
                                  minHeight: 0,
                                  width: '24px',
                                  height: '24px',
                                  background: chatbot.brandColor,
                                  '&:hover': {
                                    background: chatbot.brandColor,
                                  },
                                }}
                              >
                                <ArrowUp size={16} weight="regular" />
                              </Button>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          background: chatbot.responseContainer,
                          color: chatbot.responseText,
                          borderRadius: '8px',

                          '& .MuiInputBase-root:hover fieldset': {
                            borderColor: chatbot.brandColor,
                          },
                          '& .MuiInputBase-root:focus fieldset': {
                            borderColor: chatbot.brandColor,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: chatbot.brandColor,
                          },
                          '& .MuiInputBase-root fieldset': {
                            borderColor: chatbot.inputBorderColor,
                          },
                        }}
                      />

                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 4,
                          marginTop: '8px',
                        }}
                      >
                        {chatbot?.chatbotDropdowns?.map((item, index) => (
                          <Select
                            sx={{
                              background: chatbot.responseContainer,
                              color: chatbot.responseText,
                              '& fieldset': {
                                border: 'none',
                              },
                            }}
                            key={item.varInputNodeId}
                            value={dropdownValues[index]}
                            onChange={e => {
                              setDropdownValues(prev => {
                                const newValues = [...prev];
                                newValues[index] = e.target.value;
                                return newValues;
                              });
                            }}
                          >
                            {item.options.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              </MainChatArea>
            </SplitScreenContainer>
          </ChatContainer>
        )}
      </Drawer>

      <Popover
        id="fluxbot-dropdown"
        open={isFluxbotDropdownPopoverOpen}
        onClose={() => {
          setIsFluxbotDropdownPopoverOpen(false);
        }}
        anchorEl={tabDropdownRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <FluxbotDropdown
          fluxBots={fluxBots}
          activeTab={activeTab}
          onClickSelectFluxBot={item => {
            setIsFluxbotDropdownPopoverOpen(false);
            setActiveTab({
              id: item.id,
              name: item.name,
              space: item.space,
            });
            saveChatFlowId(item.id);
          }}
        />
      </Popover>

      <Popover
        id="fluxbot-settings"
        open={isSettingsPopoverOpen}
        onClose={() => {
          setIsSettingsPopoverOpen(false);
        }}
        anchorEl={settingsPopoverAnchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <List>
          {settingsOptions.map(option => (
            <ListItem disablePadding key={option.key}>
              <ListItemButton
                disabled={option.disabled}
                onClick={() => {
                  setIsSettingsPopoverOpen(false);
                  option.action();
                }}
              >
                <ListItemIcon sx={{ fontSize: 14 }}>{option.icon}</ListItemIcon>

                <ListItemText sx={{ fontSize: 14 }}>
                  {option.label}
                </ListItemText>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Popover>

      <Popover
        open={isHistoryPopoverOpen}
        onClose={() => setIsHistoryPopoverOpen(false)}
        anchorEl={historyPopoverAnchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <History
          chatbot={chatbot}
          currentSessionId={session}
          onChangeSession={sessionId => {
            getChatSession(sessionId);
          }}
        />
      </Popover>

      {isSettingsOpen && (
        <PublishChatModal
          show={isSettingsOpen}
          onHide={() => setIsSettingsOpen(false)}
          setFlow={handleSetChatbot}
          chatData={chatbot}
        />
      )}
    </Box>
  );
};

export default ChatBot;